import type { Application, Table } from '@/api/types'
import { hashId } from '@/shared/lib/hash-id'

/**
 * Cache updaters for a database's tables.
 *
 * Tables are not a list of their own here: the applications endpoint returns
 * them nested on the database application, and that is the copy the sidebar
 * renders from, so it is the copy the realtime layer keeps current.
 *
 * Ported from `web-frontend/modules/database/store/table.js`.
 */

/** Narrowed to the application type that has tables. */
type DatabaseApplication = Extract<Application, { type: 'database' }>

function isDatabase(application: Application): application is DatabaseApplication {
  return application.type === 'database'
}

function withTables(
  applications: Application[],
  databaseId: number,
  update: (tables: Table[]) => Table[],
): Application[] {
  const index = applications.findIndex(
    (application) => application.id === databaseId && isDatabase(application),
  )
  if (index === -1) return applications

  const database = applications[index] as DatabaseApplication
  // Passed as-is, not copied: the updaters never mutate, and they signal "no
  // change" by returning the same array. Handing them a copy makes that signal
  // impossible and rewrites the application on every unrelated table event.
  const tables = update(database.tables)
  if (tables === database.tables) return applications

  const next = [...applications]
  next[index] = { ...database, tables }
  return next
}

export function upsertTable(
  applications: Application[],
  databaseId: number,
  table: Table,
): Application[] {
  return withTables(applications, databaseId, (tables) => {
    const index = tables.findIndex((existing) => existing.id === table.id)
    if (index === -1) return [...tables, table]

    // Upsert rather than insert: `table_created` also fires for a table
    // restored from the trash, which this client may still be holding.
    const next = [...tables]
    next[index] = table
    return next
  })
}

export function updateTable(
  applications: Application[],
  databaseId: number,
  tableId: number,
  values: Partial<Table>,
): Application[] {
  return withTables(applications, databaseId, (tables) => {
    const index = tables.findIndex((table) => table.id === tableId)
    if (index === -1) return tables

    const next = [...tables]
    next[index] = { ...tables[index]!, ...values }
    return next
  })
}

export function removeTable(
  applications: Application[],
  databaseId: number,
  tableId: number,
): Application[] {
  return withTables(applications, databaseId, (tables) => {
    const next = tables.filter((table) => table.id !== tableId)
    return next.length === tables.length ? tables : next
  })
}

/**
 * Renumbers the tables of the database whose id hashes to `hashedDatabaseId`.
 *
 * Both the database id and the order arrive hashed -- see `hashId`. A table the
 * ordering does not mention keeps the order it had, for the same reason as
 * applications: the ordering covers the database, so a table missing from it is
 * one this client should not be moving.
 */
export function orderTables(
  applications: Application[],
  hashedDatabaseId: string,
  hashedOrder: string[],
): Application[] {
  const database = applications.find(
    (application) => hashId(application.id) === hashedDatabaseId,
  )
  if (database === undefined) return applications

  return withTables(applications, database.id, (tables) =>
    tables.map((table) => {
      const index = hashedOrder.indexOf(hashId(table.id))
      return index === -1 ? table : { ...table, order: index + 1 }
    }),
  )
}
